// Una tarjeta del catálogo. Abre el detalle al hacer clic, y «Generar ahora» va aparte porque el
// permiso y la tabla del lake lo pueden apagar sin apagar la tarjeta.
import { cn } from '@/lib/cn';
import { formatDateMx, formatTimeMx } from '@/lib/dateHelpers';
import { NoSource } from '@/shared/patterns/NoSource';
import { JOB_STATUS_LABEL, PERIODICITY_CHIP, PERIODICITY_LABEL } from '../logic';
import type { ReportCard } from '../types';

export function ReportCardTile({
    card,
    onOpen,
    onRun,
    isRunning,
}: {
    card: ReportCard;
    onOpen: () => void;
    onRun: () => void;
    isRunning: boolean;
}) {
    const { definition, blockedReason, lastRunAt, lastRunStatus } = card;
    const lastRunIso = lastRunAt === null ? null : new Date(lastRunAt).toISOString();

    return (
        <button
            type="button"
            onClick={onOpen}
            className={cn(
                'flex flex-col gap-1.5 rounded-md border border-border bg-card p-3 text-left transition-colors hover:bg-accent/40',
                definition.category === 'REGULATORY' &&
                    'border-warning-soft-border bg-warning-soft/40',
            )}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-[12.5px] font-bold leading-tight">{definition.name}</div>
                    <div className="mt-px font-mono text-[9.5px] text-muted-foreground">
                        {/* eslint-disable-next-line no-restricted-syntax -- código de negocio del reporte (`RSK-01`), el que se cita en los documentos */}
                        {definition.reportId} · {definition.reference}
                    </div>
                </div>
                <div className="flex flex-none flex-col items-end gap-1">
                    <span
                        className={cn(
                            'rounded-full px-1.5 py-px text-[9.5px] font-bold uppercase tracking-[0.04em]',
                            PERIODICITY_CHIP[definition.periodicity],
                        )}
                    >
                        {PERIODICITY_LABEL[definition.periodicity]}
                    </span>
                    {definition.regulatoryMandatory ? (
                        <span className="rounded bg-warning px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.04em] text-white">
                            Obligatorio
                        </span>
                    ) : null}
                </div>
            </div>

            <p className="text-[11px] leading-snug text-muted-foreground">
                {definition.description}
            </p>

            <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
                <span>
                    {lastRunIso
                        ? `Última corrida ${formatDateMx(lastRunIso)} · ${formatTimeMx(lastRunIso)}`
                        : 'Nunca se ha corrido'}
                </span>
                {lastRunStatus ? <span>· {JOB_STATUS_LABEL[lastRunStatus]}</span> : null}
                <span>·</span>
                <span>
                    Quién la corrió:{' '}
                    <NoSource reason="El historial de corridas no dice quién lanzó cada una." />
                </span>
            </div>

            {definition.regulatoryMandatory ? (
                <div className="text-[10px] text-warning">
                    Próximo vencimiento:{' '}
                    <NoSource reason="Ningún servicio publica el calendario regulatorio: el motor de reportes no conoce fechas de entrega." />
                </div>
            ) : null}

            <div className="mt-1.5 flex gap-1.5">
                <span
                    role="button"
                    tabIndex={blockedReason ? -1 : 0}
                    aria-disabled={blockedReason !== null || isRunning}
                    title={blockedReason ?? 'Corre el reporte con corte al día de hoy'}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (blockedReason || isRunning) return;
                        onRun();
                    }}
                    onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.stopPropagation();
                        if (blockedReason || isRunning) return;
                        onRun();
                    }}
                    className={cn(
                        'inline-flex items-center rounded bg-primary px-2 py-[3px] text-[9.5px] font-semibold text-primary-foreground transition-opacity',
                        blockedReason || isRunning
                            ? 'cursor-not-allowed opacity-60'
                            : 'cursor-pointer hover:opacity-90',
                    )}
                >
                    {isRunning ? 'Generando…' : 'Generar ahora'}
                </span>
            </div>
        </button>
    );
}
